import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Badge, Button, ChipGroup, EmptyState, PageHeader, Spinner } from "@magarac/ui";
import { jobPageSchema, type JobStatus } from "@magarac/shared/schemas/job";
import { HelpIcon } from "../../../components/HelpIcon";
import { NewJobDialog } from "../../../components/NewJobDialog";
import { api } from "../../../lib/api";
import { formatLocalDate, JOB_STATUS_LABEL } from "../../../lib/format";
import { useIsOffice, useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/jobs/")({
  component: JobsPage,
});

type Filter = "all" | "owes" | JobStatus;

/** CRM-11 list. Members see only their jobs (the server narrows it). Screen key: jobs.list */
function JobsPage() {
  const { active } = useTenant();
  const office = useIsOffice();
  const [filter, setFilter] = useState<Filter>(office ? "unscheduled" : "scheduled");
  const [adding, setAdding] = useState(false);
  const q = useInfiniteQuery({
    queryKey: ["jobs", active?.id, "list", filter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.get("/jobs", jobPageSchema, {
        query: {
          limit: 50,
          cursor: pageParam,
          ...(filter === "owes" ? { owes: "true" } : filter === "all" ? {} : { status: filter }),
        },
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(active),
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const filters: ReadonlyArray<{ value: Filter; label: string }> = [
    { value: "unscheduled", label: JOB_STATUS_LABEL.unscheduled.label },
    { value: "scheduled", label: JOB_STATUS_LABEL.scheduled.label },
    { value: "in_progress", label: JOB_STATUS_LABEL.in_progress.label },
    { value: "complete", label: JOB_STATUS_LABEL.complete.label },
    { value: "all", label: "All" },
    ...(office ? [{ value: "owes" as const, label: "Owes money" }] : []),
  ];
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={office ? "Jobs" : "My jobs"}
        help={<HelpIcon screen="jobs.list" />}
        actions={office ? <Button onClick={() => setAdding(true)}>New job</Button> : null}
      />
      <div className="mb-4">
        <ChipGroup label="Show" value={filter} onChange={setFilter} options={filters} />
      </div>
      {q.isLoading ? <Spinner /> : null}
      {q.data && items.length === 0 ? <EmptyState title="No jobs here" /> : null}
      <ul className="divide-y divide-line rounded-card border border-line bg-surface empty:hidden">
        {items.map((j) => (
          <li key={j.id}>
            <Link
              to="/jobs/$id"
              params={{ id: j.id }}
              className="flex flex-wrap items-center justify-between gap-2 p-4 hover:bg-brand-50"
            >
              <div className="min-w-0">
                <div className="font-medium">
                  Job #{j.number} · {j.customerName}
                </div>
                <div className="text-sm text-muted">
                  {j.jobTypeName}
                  {j.dueOn ? ` · due ${formatLocalDate(j.dueOn)}` : ""}
                  {j.description ? ` · ${j.description.slice(0, 60)}` : ""}
                </div>
              </div>
              <span className="flex gap-1">
                {j.urgency === "urgent" ? <Badge tone="danger">Urgent</Badge> : null}
                <Badge tone={JOB_STATUS_LABEL[j.status].tone}>
                  {JOB_STATUS_LABEL[j.status].label}
                </Badge>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {q.hasNextPage ? (
        <Button
          variant="ghost"
          className="mt-3"
          onClick={() => void q.fetchNextPage()}
          busy={q.isFetchingNextPage}
        >
          Show more
        </Button>
      ) : null}
      {office ? <NewJobDialog open={adding} onClose={() => setAdding(false)} /> : null}
    </div>
  );
}
